// CardForm - form for creating/editing cards with character counter
// Implements US-2.1: Card text input with 1-250 character validation

import React, { useState, FormEvent, ChangeEvent } from 'react';
import styles from './CardForm.module.css';

interface CardFormProps {
  initialText?: string;
  onSubmit: (text: string) => void;
  onCancel: () => void;
  submitLabel?: string;
}

export const CardForm: React.FC<CardFormProps> = ({
  initialText = '',
  onSubmit,
  onCancel,
  submitLabel = 'Karte hinzufügen',
}) => {
  const [text, setText] = useState(initialText);
  const [error, setError] = useState<string | null>(null);

  const charCount = text.length;
  const maxChars = 250;
  const minChars = 1;

  // HANDLE text input changes
  const handleTextChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;

    // ENFORCE maximum character limit
    if (newText.length <= maxChars) {
      setText(newText);
      setError(null);
    }
  };

  // HANDLE form submission
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    // VALIDATE text length
    if (text.length < minChars) {
      setError('Kartentext muss mindestens 1 Zeichen lang sein');
      return;
    }

    if (text.length > maxChars) {
      setError(`Kartentext darf maximal ${maxChars} Zeichen haben`);
      return;
    }

    // SUBMIT card text
    onSubmit(text.trim());

    // RESET form
    setText('');
    setError(null);
  };

  // HANDLE cancel
  const handleCancel = () => {
    setText(initialText);
    setError(null);
    onCancel();
  };

  // DETERMINE character counter color
  const getCounterClass = () => {
    if (charCount === 0) return styles.charCounter;
    if (charCount > maxChars * 0.9) return `${styles.charCounter} ${styles.warning}`;
    return styles.charCounter;
  };

  const isValid = charCount >= minChars && charCount <= maxChars;

  return (
    <form className={styles.cardForm} onSubmit={handleSubmit}>
      <div className={styles.formGroup}>
        <textarea
          className={styles.cardTextarea}
          value={text}
          onChange={handleTextChange}
          placeholder="Gib deinen Kartentext ein..."
          rows={3}
          autoFocus
        />
        <div className={getCounterClass()}>
          {charCount} / {maxChars}
        </div>
      </div>

      {error && <div className={styles.formError}>{error}</div>}

      <div className={styles.formActions}>
        <button
          type="submit"
          className={`${styles.btn} ${styles.btnPrimary}`}
          disabled={!isValid}
        >
          {submitLabel}
        </button>
        <button
          type="button"
          className={`${styles.btn} ${styles.btnSecondary}`}
          onClick={handleCancel}
        >
          Abbrechen
        </button>
      </div>
    </form>
  );
};
